import { RobotoPrincipalType } from "@/shared/principal";

/**
 * Human-facing label for each principal type — the operator pill text and the
 * "any &lt;type&gt;" chip prose.
 *
 * Exhaustive over {@link RobotoPrincipalType} on purpose: a new platform
 * principal type fails to compile here until it gets a label, rather than
 * rendering a blank pill. Note that a `created_by` / `modified_by` column can
 * only ever hold `user` / `device` / `invocation` (see
 * `identity.audit_field_principal` in `roboto_service`), so `Integration` and
 * `Org` are labeled for completeness, not because a filter offers them today.
 */
export const IDENTITY_TYPE_LABELS: Record<RobotoPrincipalType, string> = {
  [RobotoPrincipalType.User]: "User",
  [RobotoPrincipalType.Device]: "Device",
  [RobotoPrincipalType.Invocation]: "Invocation",
  [RobotoPrincipalType.Integration]: "Integration",
  [RobotoPrincipalType.Org]: "Org",
};

/**
 * The `<type>:` prefix every principal of this type carries — e.g. `user:`
 * heads every `user:<id>` value. Used to build both the fully-qualified option
 * values and the "any of this type" LIKE pattern.
 */
export function identityTypePrefix(type: RobotoPrincipalType): string {
  return `${type}:`;
}

/**
 * The LIKE pattern that matches every principal of a type: the `<type>:`
 * prefix followed by the `%` wildcard (e.g. `user:%`). Left-anchored, so it
 * reads as a prefix match. The type prefix contains no LIKE metacharacters, so
 * only the trailing `%` is a wildcard.
 */
export function identityTypeLikePattern(type: RobotoPrincipalType): string {
  return `${identityTypePrefix(type)}%`;
}

/**
 * Chip prose for an "any of this type" selection, e.g. `any user`. Lower-cased
 * so the chip reads naturally ("Created By is any user").
 */
export function anyIdentityTypeLabel(type: RobotoPrincipalType): string {
  return `any ${IDENTITY_TYPE_LABELS[type].toLowerCase()}`;
}
